import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, 
  Truck, 
  MapPin, 
  Phone, 
  Mail, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Shield
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { settingsAPI, SiteSettings } from '../services/api';
import PaymentMethods from './PaymentMethods';

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    let active = true;
    settingsAPI
      .getSettings()
      .then((res) => {
        if (active) setSettings(res.data);
      })
      .catch(() => {
        // Footer still renders without contact info if the request fails.
      });
    return () => {
      active = false;
    };
  }, []);

  const hasContact =
    settings &&
    (settings.phone ||
      settings.email ||
      settings.address_line1 ||
      settings.address_line2 ||
      settings.address_country);

  return (
    <footer className="bg-neutral-900 text-white">
      {/* Main Footer Content */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-8 lg:gap-12">
          
          {/* About Us Column - Balanced */}
          <div className="lg:col-span-1">
            <p className="text-neutral-300 text-sm leading-relaxed mb-6">
              {t.footer.about.description}
            </p>
            
            {/* Trust Indicators */}
            <div className="space-y-3">
              <div className="flex items-center text-neutral-300 text-sm">
                <Truck className="w-4 h-4 mr-2 text-neutral-400" />
                {t.footer.about.fastDelivery}
              </div>
              <div className="flex items-center text-neutral-300 text-sm">
                <Shield className="w-4 h-4 mr-2 text-neutral-400" />
                {t.footer.about.securePayment}
              </div>
            </div>

            {/* Accepted payment cards */}
            <div className="mt-6">
              <PaymentMethods className="h-7" />
            </div>
          </div>

          {/* Customer Service Column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6 flex items-center">
              <HelpCircle className="w-5 h-5 mr-2 text-neutral-400" />
              {t.footer.customerService.title}
            </h3>
            
            <div className="space-y-3">
              <Link 
                to="/shipping" 
                className="block text-neutral-300 hover:text-white transition-colors duration-200 text-sm"
              >
                {t.footer.customerService.shippingDelivery}
              </Link>
            </div>
          </div>

          {/* Contact Us Column */}
          {hasContact && (
            <div className="lg:col-span-1">
              <h3 className="text-lg font-semibold mb-6 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-neutral-400" />
                {t.footer.contact.title}
              </h3>

              {/* Contact Info (managed from admin settings) */}
              <div className="space-y-4">
                {settings?.phone && (
                  <div className="flex items-start space-x-3">
                    <Phone className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                    <div>
                      <p className="text-neutral-300 text-sm">{settings.phone}</p>
                      {settings.phone_hours && (
                        <p className="text-neutral-400 text-xs">{settings.phone_hours}</p>
                      )}
                    </div>
                  </div>
                )}

                {settings?.email && (
                  <div className="flex items-start space-x-3">
                    <Mail className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                    <div>
                      <p className="text-neutral-300 text-sm">{settings.email}</p>
                      {settings.email_note && (
                        <p className="text-neutral-400 text-xs">{settings.email_note}</p>
                      )}
                    </div>
                  </div>
                )}

                {(settings?.address_line1 || settings?.address_line2 || settings?.address_country) && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                    <div>
                      {settings?.address_line1 && (
                        <p className="text-neutral-300 text-sm">{settings.address_line1}</p>
                      )}
                      {settings?.address_line2 && (
                        <p className="text-neutral-300 text-sm">{settings.address_line2}</p>
                      )}
                      {settings?.address_country && (
                        <p className="text-neutral-400 text-xs">{settings.address_country}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-neutral-800">
        <div className="container-custom py-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            {/* Copyright */}
            <div className="text-neutral-400 text-sm">
              {t.footer.bottom.copyright}
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <Link 
                to="/privacy" 
                className="inline-flex items-center min-h-[24px] py-1 text-neutral-400 hover:text-white transition-colors duration-200"
              >
                {t.footer.bottom.privacyPolicy}
              </Link>
              <Link 
                to="/terms" 
                className="inline-flex items-center min-h-[24px] py-1 text-neutral-400 hover:text-white transition-colors duration-200"
              >
                {t.footer.bottom.termsOfService}
              </Link>
              <Link 
                to="/cookies" 
                className="inline-flex items-center min-h-[24px] py-1 text-neutral-400 hover:text-white transition-colors duration-200"
              >
                {t.footer.bottom.cookiePolicy}
              </Link>
            </div>

            {/* Social Media (each icon shows only when a URL is configured) */}
            {(settings?.facebook_url || settings?.instagram_url || settings?.linkedin_url) && (
              <div className="flex items-center space-x-4">
                {settings?.facebook_url && (
                  <a
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center min-h-[24px] min-w-[24px] text-neutral-400 hover:text-white transition-colors duration-200"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {settings?.instagram_url && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center min-h-[24px] min-w-[24px] text-neutral-400 hover:text-white transition-colors duration-200"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {settings?.linkedin_url && (
                  <a
                    href={settings.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center min-h-[24px] min-w-[24px] text-neutral-400 hover:text-white transition-colors duration-200"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 